import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../lib/analytics";
import { autoTickForPath } from "../lib/publicQuickstart";

/**
 * Fires a `$pageview` event into PostHog on every React Router
 * location change. Skips the very first render's duplicate when
 * combined with PostHog's autocapture-pageview, but our config
 * sets `capture_pageview: false` so this hook is the single source
 * of truth.
 *
 * Iter273.8 — also auto-ticks the public Quickstart steps whose
 * `autoOn` list matches the visited pathname. Centralised here so
 * we don't mount a second router-listener.
 *
 * Mounted once at the root of the app (inside <BrowserRouter>).
 * Returns null — no DOM output.
 */
export default function RouteTracker() {
  const location = useLocation();
  const lastPathRef = useRef(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;
    trackPageView(location.pathname, { search: location.search });

    // Iter273.8 — auto-tick public-Quickstart steps. Guard against
    // auth-gated and brand-segmented surfaces so we don't tick a
    // step the visitor never actually saw the public surface for.
    const p = location.pathname || "";
    if (!p.startsWith("/admin")
        && !p.startsWith("/qor-ops")
        && !p.startsWith("/account")
        && !p.startsWith("/qorbom")) {
      autoTickForPath(p);
    }
  }, [location.pathname, location.search]);

  return null;
}
